import { Icon } from '@chakra-ui/react';
import { BasePagination, IconButtonWithTooltip, PenIcon, PlusIcon } from '@elkaso-app/web-design';
import { useGetExclusiveDealsApi } from 'apis/exclusive-deals';
import { BaseTableContainer, ExcelTable } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { ActivationFilterButton } from 'components/base-table/filters';
import { BaseSortButton } from 'components/base-table/sorting';
import { setHiddenColumns } from 'components/base-table/utils';
import { Can } from 'configs/can';
import { usePageParams } from 'hooks/use-page-params';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import {
  AddExclusiveDealModal,
  IAddExclusiveDealModalCell,
} from 'pages/exclusive-deals/components/add-exclusive-deal-modal';
import { DealHistoryModal, IDealHistoryModalCell } from 'pages/exclusive-deals/components/deal-history-modal';
import { DealsSheetModal } from 'pages/exclusive-deals/components/deals-sheet-modal';
import {
  EditProductCategoryModal,
  IEditProductCategoryModalCell,
} from 'pages/exclusive-deals/components/edit-product-category-modal';
import {
  EditProductInfoModal,
  IEditProductInfoModalCell,
} from 'pages/exclusive-deals/components/edit-product-info-modal';
import { EditableDealsGrid } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deals-grid';
import {
  ExclusiveDealActivationSwitch,
  IExclusiveDealActivationSwitchCell,
} from 'pages/exclusive-deals/components/exclusive-deal-activation-switch';
import { LastActiveFilterButton } from 'pages/exclusive-deals/components/last-active-filter-button';
import { LastModifiedFilterButton } from 'pages/exclusive-deals/components/last-modified-filter-button';
import { ProductNameFilterButton } from 'pages/exclusive-deals/components/product-name-filter-button';
import { SupplierFilterButton } from 'pages/exclusive-deals/components/supplier-filter-button';
import React, { useEffect, useState } from 'react';
import { FiClock, FiEdit, FiFileText, FiFolder, FiPackage } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const ExclusiveDealsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const { getPageParams, setPageParams } = usePageParams();
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, data } = useGetExclusiveDealsApi();

  const { pageCount, total, data: dealsData } = data ?? {};

  const [mode, setMode] = useState<'read' | 'edit'>('read');

  useEffect(() => {
    dispatch(changeTitle('Exclusive Deals'));
  }, []);

  // Strip legacy ?sort=startDate|endDate from URL: BE no longer accepts sort on removed columns
  useEffect(() => {
    const params = getPageParams();
    const sort = typeof params.sort === 'string' ? params.sort : '';
    if (sort.startsWith('startDate.') || sort.startsWith('endDate.')) {
      const nextParams = { ...params };
      delete nextParams.sort;
      setPageParams(nextParams);
    }
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Supplier'
            filterButtonComponent={<SupplierFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='supplierName' />}
          />
        ),
        accessor: 'supplier',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplier']>) =>
          value ? (
            <TableCell value={value.name ?? `Supplier #${value.id}`} color={value.name ? 'gray.600' : 'gray.400'} />
          ) : (
            <TableCell value='—' color='gray.400' />
          ),
      },
      {
        Header: (
          <TableHeader
            name='Product'
            filterButtonComponent={<ProductNameFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='productName' />}
          />
        ),
        accessor: 'product',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['product']>) => (
          <TableCell value={value?.nameEn || '-'} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Deal Price' />,
        accessor: 'dealPrice',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['dealPrice']>) => (
          <TableCell value={value != null ? String(value) : '-'} />
        ),
      },
      {
        Header: <TableHeader name='UOM' />,
        accessor: 'uom',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['uom']>) => <TableCell value={value || '-'} />,
      },
      {
        Header: <TableHeader name='Brand' />,
        accessor: 'brand',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['brand']>) => <TableCell value={value || '-'} />,
      },
      {
        Header: <TableHeader name='Origin' />,
        accessor: 'origin',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['origin']>) => <TableCell value={value || '-'} />,
      },
      {
        Header: <TableHeader name='State' />,
        accessor: 'state',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['state']>) => <TableCell value={value || '-'} />,
      },
      {
        Header: <TableHeader name='Spec' />,
        accessor: 'spec',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['spec']>) => <TableCell value={value || '-'} />,
      },
      {
        Header: <TableHeader name='Created' sortButtonComponent={<BaseSortButton sortBy='createdAt' />} />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Last Modified'
            filterButtonComponent={<LastModifiedFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='lastModifiedAt' />}
          />
        ),
        accessor: 'lastModifiedAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['lastModifiedAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value ?? ''} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Last Active'
            filterButtonComponent={<LastActiveFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='lastActiveAt' />}
          />
        ),
        accessor: 'lastActiveAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['lastActiveAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value ?? ''} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Status'
            filterButtonComponent={<ActivationFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='isActive' />}
          />
        ),
        accessor: 'activation',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['activation']>) => (
          <CustomTableCell<IExclusiveDealActivationSwitchCell>
            as={ExclusiveDealActivationSwitch}
            dealId={value?.id}
            isActive={value?.isActive}
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            <CustomTableCell<IAddExclusiveDealModalCell>
              as={AddExclusiveDealModal}
              variant='edit'
              deal={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Edit'
                  aria-label='edit-deal-btn'
                  Icon={<PenIcon />}
                  variant='unstyled'
                />
              }
            />
            <CustomTableCell<IEditProductInfoModalCell>
              as={EditProductInfoModal}
              deal={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Edit Product Info'
                  aria-label='edit-product-info-btn'
                  Icon={<Icon as={FiPackage} />}
                  variant='unstyled'
                />
              }
            />
            <CustomTableCell<IEditProductCategoryModalCell>
              as={EditProductCategoryModal}
              deal={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Edit Product Category'
                  aria-label='edit-product-category-btn'
                  Icon={<Icon as={FiFolder} />}
                  variant='unstyled'
                />
              }
            />
            <CustomTableCell<IDealHistoryModalCell>
              as={DealHistoryModal}
              deal={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='View History'
                  aria-label='view-history-btn'
                  Icon={<Icon as={FiClock} />}
                  variant='unstyled'
                />
              }
            />
          </div>
        ),
      },
    ],
    []
  );

  interface IColumnsAccessorData {
    id: number;
    product: any;
    supplier: { id: number; name: string | null } | null;
    dealPrice: number | null;
    uom: string | null;
    brand: string | null;
    origin: string | null;
    state: string | null;
    spec: string | null;
    createdAt: string;
    lastModifiedAt: string | null;
    lastActiveAt: string | null;
    activation: any;
    actions: any;
  }

  const columnsAccessorData = React.useMemo(
    () =>
      dealsData?.map((deal): IColumnsAccessorData => {
        return {
          id: deal?.id,
          product: deal?.product,
          supplier: deal?.supplier ?? null,
          dealPrice: deal?.dealPrice ?? null,
          uom: deal?.product?.defaultUnit ?? null,
          brand: deal?.product?.brandEn ?? null,
          origin: deal?.product?.origin ?? null,
          state: deal?.product?.state ?? null,
          spec: deal?.product?.specs ?? null,
          createdAt: deal?.createdAt,
          lastModifiedAt: deal?.lastModifiedAt ?? null,
          lastActiveAt: deal?.lastActiveAt ?? null,
          activation: deal,
          actions: deal,
        };
      }),
    [dealsData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Exclusive Deals' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          {mode === 'read' && <ClearFilterButton />}

          {mode === 'read' && (
            <DealsSheetModal
              trigger={
                <IconButtonWithTooltip
                  label='Import / Export deals sheet'
                  aria-label='deals-sheet-btn'
                  Icon={<Icon as={FiFileText} />}
                />
              }
            />
          )}

          <Can I='manage' a='all'>
            {mode === 'read' && (
              <IconButtonWithTooltip
                label='Bulk Edit'
                aria-label='bulk-edit-btn'
                Icon={<Icon as={FiEdit} />}
                onClick={() => setMode('edit')}
              />
            )}
            {mode === 'read' && (
              <AddExclusiveDealModal
                variant='add'
                trigger={
                  <IconButtonWithTooltip label='Add exclusive deal' aria-label='add-deal-btn' Icon={<PlusIcon />} />
                }
              />
            )}
          </Can>
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        {mode === 'edit' ? (
          <EditableDealsGrid deals={dealsData ?? []} onDone={() => setMode('read')} />
        ) : (
          <ExcelTable
            columns={columns}
            data={columnsAccessorData}
            initialHiddenColumns={setHiddenColumns(['activation', 'actions'])}
          />
        )}
      </BaseTableContainer.TableWrapper>

      {mode === 'read' && (
        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      )}
    </BaseTableContainer>
  );
};
